import React, { useState, useEffect } from 'react';
import { Select, Button as AntButton, Card, Typography, Row, Col, Spin, Alert, Divider } from 'antd';
import { SearchOutlined, ReloadOutlined, ProjectOutlined, PartitionOutlined, CreditCardOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { FETCH_PROYECTO_PATH } from '../../services/path';

const { Option } = Select;
const { Title, Text } = Typography;

const SELECTOR_TYPES = {
  proyecto: {
    icon: ProjectOutlined,
    label: 'Proyecto',
    description: 'Analizar datos por proyecto completo',
    color: '#1890ff'
  },
  elemento: {
    icon: PartitionOutlined,
    label: 'Elemento',
    description: 'Analizar datos por elemento específico',
    color: '#52c41a'
  },
  tarjeta: {
    icon: CreditCardOutlined,
    label: 'Tarjeta',
    description: 'Analizar datos por tarjeta de control',
    color: '#fa8c16'
  }
};

const SankeySelector = ({ onSelectionChange }) => {
  const [selectorType, setSelectorType] = useState('proyecto');
  const [selectedId, setSelectedId] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar datos según el tipo seleccionado
  const loadData = async (type) => {
    setLoading(true);
    setError(null);
    try {
      let response;
      let dataArray = [];
      
      switch (type) {
        case 'proyecto':
          response = await api.get(`${FETCH_PROYECTO_PATH}?perPage=1000&offSet=0`);
          dataArray = response?.data?.proyectos || response?.data?.data || [];
          break;
        case 'elemento':
          try {
            // Intentar diferentes endpoints posibles para elementos
            let elementoResponse;
            try {
              elementoResponse = await api.get('/api/elemento?perPage=1000&offSet=0');
            } catch (e1) {
              try {
                elementoResponse = await api.get('/elemento?perPage=1000&offSet=0');
              } catch (e2) {
                try {
                  elementoResponse = await api.get('/api/elemento?perPage=1000&offSet=0');
                } catch (e3) {
                  throw new Error('No se pudo encontrar el endpoint de elementos');
                }
              }
            }
            dataArray = elementoResponse?.data?.elementos || elementoResponse?.data?.data || [];
          } catch (elemError) {
            console.warn('Endpoint de elementos no disponible:', elemError);
            setError('El módulo de elementos no está disponible actualmente. Por favor, usa el análisis por proyecto.');
            dataArray = [];
          }
          break;
        case 'tarjeta':
          try {
            // Intentar diferentes endpoints posibles para tarjetas
            let tarjetaResponse;
            try {
              tarjetaResponse = await api.get('/api/tarjeta?perPage=1000&offSet=0');
            } catch (e1) {
              try {
                tarjetaResponse = await api.get('/tarjeta?perPage=1000&offSet=0');
              } catch (e2) {
                try {
                  tarjetaResponse = await api.get('/api/cards?perPage=1000&offSet=0');
                } catch (e3) {
                  throw new Error('No se pudo encontrar el endpoint de tarjetas');
                }
              }
            }
            dataArray = tarjetaResponse?.data?.tarjetas || tarjetaResponse?.data?.data || [];
          } catch (tarjetaError) {
            console.warn('Endpoint de tarjetas no disponible:', tarjetaError);
            setError('El módulo de tarjetas no está disponible actualmente. Por favor, usa el análisis por proyecto.');
            dataArray = [];
          }
          break;
        default:
          throw new Error(`Tipo no válido: ${type}`);
      }
      
      setData(dataArray);
    } catch (error) {
      console.error(`Error al cargar ${type}s:`, error);
      setError(`Error al cargar la lista de ${type}s: ${error.message}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos cuando cambia el tipo
  useEffect(() => {
    loadData(selectorType);
    // Reset selection when type changes
    setSelectedId(null);
  }, [selectorType]);

  const handleTypeChange = (type) => {
    setSelectorType(type);
  };

  const handleItemSelect = (value, option) => {
    setSelectedId(value);
    const nameElement = option.children.props ? option.children.props.children[0] : option.children;
    const name = nameElement.props ? nameElement.props.children : nameElement;
    
    // Notificar al componente padre
    onSelectionChange({
      type: selectorType,
      id: value,
      name: name
    });
  };

  const handleRefresh = () => {
    loadData(selectorType);
  };

  const filterOption = (input, option) => {
    const searchValue = input.toLowerCase();
    const itemText = option.children.toString().toLowerCase();
    return itemText.includes(searchValue);
  };

  const renderTypeSelector = () => (
    <div style={{ marginBottom: '1.5rem' }}>
      <Text strong style={{ display: 'block', marginBottom: '1rem', fontSize: '1rem' }}>
        Tipo de Análisis:
      </Text>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem'
      }}>
        {Object.entries(SELECTOR_TYPES).map(([key, config]) => {
          const IconComponent = config.icon;
          const isSelected = selectorType === key;
          
          return (
            <div
              key={key}
              onClick={() => handleTypeChange(key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTypeChange(key);
                }
              }}
              tabIndex={0}
              role="button"
              aria-pressed={isSelected}
              style={{
                padding: '1rem',
                border: `2px solid ${isSelected ? config.color : '#e9ecef'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: isSelected ? `${config.color}10` : 'white',
                textAlign: 'center'
              }}
              onMouseOver={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = config.color;
                  e.currentTarget.style.backgroundColor = `${config.color}05`;
                }
              }}
              onMouseOut={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.backgroundColor = 'white';
                }
              }}
              onFocus={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = config.color;
                  e.currentTarget.style.backgroundColor = `${config.color}05`;
                }
              }}
              onBlur={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.backgroundColor = 'white';
                }
              }}
            >
              <IconComponent 
                style={{ 
                  fontSize: '1.5rem', 
                  color: isSelected ? config.color : '#8c8c8c',
                  marginBottom: '0.5rem'
                }} 
              />
              <div>
                <Text strong style={{ 
                  display: 'block', 
                  color: isSelected ? config.color : '#000',
                  marginBottom: '0.25rem'
                }}>
                  {config.label}
                </Text>
                <Text type="secondary" style={{ fontSize: '0.85rem' }}>
                  {config.description}
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderItemSelector = () => {
    const config = SELECTOR_TYPES[selectorType];
    
    if (error) {
      return (
        <Alert
          message="Funcionalidad no disponible"
          description={error}
          type="warning"
          showIcon
          style={{ marginTop: '1rem' }}
          action={
            <AntButton size="small" onClick={handleRefresh}>
              Reintentar
            </AntButton>
          }
        />
      );
    }
    
    return (
      <div>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={18} md={20}>
            <Text strong>Seleccionar {config.label}:</Text>
            <Select
              value={selectedId}
              onChange={handleItemSelect}
              style={{ width: '100%', marginTop: '0.5rem' }}
              placeholder={`Buscar y seleccionar un ${config.label.toLowerCase()}...`}
              loading={loading}
              showSearch
              filterOption={filterOption}
              notFoundContent={loading ? <Spin size="small" /> : `No se encontraron ${config.label.toLowerCase()}s`}
              allowClear
              onClear={() => {
                setSelectedId(null);
                onSelectionChange({ type: null, id: null, name: '' });
              }}
            >
              {data.map((item) => (
                <Option key={item.codigo || item._id} value={item._id}>
                  <div>
                    <Text strong>{item.nombre || item.name || item.title}</Text>
                    {item.codigo && <Text type="secondary"> - {item.codigo}</Text>}
                    {(item.descripcion || item.description) && (
                      <div>
                        <Text type="secondary" style={{ fontSize: '0.85em' }}>
                          {((item.descripcion || item.description) || '').length > 60 
                            ? `${((item.descripcion || item.description) || '').substring(0, 60)}...` 
                            : (item.descripcion || item.description)}
                        </Text>
                      </div>
                    )}
                  </div>
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={6} md={4}>
            <AntButton 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={loading}
              style={{ marginTop: '1.5rem', width: '100%' }}
            >
              Actualizar
            </AntButton>
          </Col>
        </Row>

        {data.length > 0 && (
          <div style={{ marginTop: '0.5rem' }}>
            <Text type="secondary" style={{ fontSize: '0.85em' }}>
              {data.length} {config.label.toLowerCase()}s disponibles
            </Text>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      {renderTypeSelector()}
      <Divider />
      {renderItemSelector()}
      
      {!selectedId && !loading && !error && data.length > 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem',
          color: '#8c8c8c',
          marginTop: '1rem'
        }}>
          <SearchOutlined style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }} />
          <div>
            <Title level={5} type="secondary">
              Selecciona un {SELECTOR_TYPES[selectorType].label}
            </Title>
            <Text type="secondary">
              Elige un {SELECTOR_TYPES[selectorType].label.toLowerCase()} para visualizar su gráfica Sankey
            </Text>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem'
        }}>
          <Spin size="large" />
          <div style={{ marginTop: '1rem' }}>
            <Text>Cargando {SELECTOR_TYPES[selectorType].label.toLowerCase()}s disponibles...</Text>
          </div>
        </div>
      )}
    </Card>
  );
};

export default SankeySelector;